import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BulkAction,
  FunnelSectionsVisibilityContext,
} from './sections-visibility.base';

type SectionRegistryItem = {
  hasContent: boolean;
  expanded: boolean;
};

const REGISTRATION_GRACE_PERIOD_MS = 320;

/**
 * Notifies all registered listeners in a set.
 * @param listeners - Set of listener callbacks to invoke
 */
const notifyListeners = (listeners: Set<() => void>) => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* noop */
    }
  });
};

interface FunnelSectionsVisibilityProviderProps {
  children: React.ReactNode;
}

/**
 * Provides state management for visibility of funnel sections.
 * Handles registration, expansion/collapse, and bulk actions across sections.
 */
export const FunnelSectionsVisibilityProvider: React.FC<
  FunnelSectionsVisibilityProviderProps
> = ({ children }) => {
  const registryRef = useRef<Map<string, SectionRegistryItem>>(new Map());
  const listenersRef = useRef<Map<string, Set<() => void>>>(new Map());
  const hasAnySectionRef = useRef(false);
  const hasAnyContentRef = useRef(false);
  const initGraceActiveRef = useRef(false);
  const initGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [bulkAction, setBulkAction] = useState<BulkAction>(null);
  const [bulkNonce, setBulkNonce] = useState(0);
  const [allSectionsClosed, setAllSectionsClosed] = useState(true);

  /**
   * Marks that at least one section with content has been seen.
   */
  const markFirstContentSeen = useCallback(() => {
    if (hasAnyContentRef.current) return;
    hasAnyContentRef.current = true;
  }, []);

  /**
   * Schedules a grace period for section registration to allow batching
   * of initial registration events during component mount.
   */
  const scheduleRegistrationGrace = useCallback(() => {
    if (initGraceActiveRef.current) return;
    initGraceActiveRef.current = true;
    if (initGraceTimerRef.current) {
      clearTimeout(initGraceTimerRef.current);
    }
    initGraceTimerRef.current = setTimeout(() => {
      initGraceActiveRef.current = false;
    }, REGISTRATION_GRACE_PERIOD_MS);
  }, []);

  /**
   * Recomputes whether all sections are closed (have no content or are collapsed).
   */
  const recomputeAllClosed = useCallback(() => {
    const entries = Array.from(registryRef.current.values());
    if (!entries.length) {
      return true;
    }
    return entries.every((item) => !item.hasContent || item.expanded === false);
  }, []);

  /**
   * Notifies all listeners for a specific section ID.
   */
  const notify = useCallback((id: string) => {
    const listeners = listenersRef.current.get(id);
    if (listeners) {
      notifyListeners(listeners);
    }
  }, []);

  /**
   * Notifies all listeners across all registered sections.
   */
  const notifyAll = useCallback(() => {
    registryRef.current.forEach((_item, id) => notify(id));
  }, [notify]);

  /**
   * Subscribes to changes for a specific section ID.
   * @param id - Section ID to subscribe to
   * @param cb - Callback to invoke when section changes
   * @returns Unsubscribe function
   */
  const subscribe = useCallback((id: string, cb: () => void) => {
    let listeners = listenersRef.current.get(id);
    if (!listeners) {
      listeners = new Set();
      listenersRef.current.set(id, listeners);
    }
    listeners.add(cb);
    return () => {
      listeners?.delete(cb);
      if (listeners && listeners.size === 0) {
        listenersRef.current.delete(id);
      }
    };
  }, []);

  /**
   * Registers a new section or updates an existing one.
   * @param id - Unique section identifier
   * @param hasContent - Whether the section has content to display
   * @param initiallyExpanded - Initial expanded state
   */
  const registerSection = useCallback(
    (id: string, hasContent: boolean, initiallyExpanded: boolean) => {
      const registry = registryRef.current;
      const existing = registry.get(id);

      if (!existing) {
        hasAnySectionRef.current = true;
        if (hasContent) {
          markFirstContentSeen();
        }
        scheduleRegistrationGrace();
        registry.set(id, { hasContent, expanded: initiallyExpanded });
        setAllSectionsClosed(recomputeAllClosed());
        notify(id);
        return;
      }

      if (existing.hasContent !== hasContent) {
        registry.set(id, { ...existing, hasContent });
        if (hasContent) {
          markFirstContentSeen();
        }
        setAllSectionsClosed(recomputeAllClosed());
        notify(id);
      }
    },
    [
      markFirstContentSeen,
      notify,
      recomputeAllClosed,
      scheduleRegistrationGrace,
    ]
  );

  useEffect(() => {
    return () => {
      if (initGraceTimerRef.current) {
        clearTimeout(initGraceTimerRef.current);
        initGraceTimerRef.current = null;
      }
    };
  }, []);

  /**
   * Updates the expanded state for a specific section.
   * @param id - Section ID to update
   * @param expanded - New expanded state
   */
  const updateSectionExpanded = useCallback(
    (id: string, expanded: boolean) => {
      const registry = registryRef.current;
      const existing = registry.get(id);
      if (!existing || existing.expanded === expanded) {
        return;
      }
      registry.set(id, { ...existing, expanded });
      setAllSectionsClosed(recomputeAllClosed());
      notify(id);
    },
    [notify, recomputeAllClosed]
  );

  /**
   * Emits a bulk action and increments the nonce to force updates.
   */
  const emitBulkAction = useCallback((action: BulkAction) => {
    setBulkAction(action);
    setBulkNonce((nonce) => nonce + 1);
  }, []);

  /**
   * Collapses all sections.
   */
  const collapseAll = useCallback(() => {
    const registry = registryRef.current;
    registry.forEach((item, id) => {
      if (item.expanded) {
        registry.set(id, { ...item, expanded: false });
      }
    });
    emitBulkAction('collapse');
    notifyAll();
    setAllSectionsClosed(recomputeAllClosed());
  }, [emitBulkAction, notifyAll, recomputeAllClosed]);

  /**
   * Expands all sections that have content.
   */
  const expandAll = useCallback(() => {
    const registry = registryRef.current;
    registry.forEach((item, id) => {
      const nextExpanded = item.hasContent ? true : false;
      if (item.expanded !== nextExpanded) {
        registry.set(id, { ...item, expanded: nextExpanded });
      }
    });
    emitBulkAction('expand');
    notifyAll();
    setAllSectionsClosed(recomputeAllClosed());
  }, [emitBulkAction, notifyAll, recomputeAllClosed]);

  /**
   * Gets the expanded state for a section.
   * @param id - Section ID
   * @returns Whether the section is expanded (defaults to false if not found)
   */
  const getExpanded = useCallback((id: string) => {
    return registryRef.current.get(id)?.expanded ?? false;
  }, []);

  const value = useMemo(
    () => ({
      registerSection,
      updateSectionExpanded,
      expandAll,
      collapseAll,
      bulkAction,
      bulkNonce,
      allSectionsClosed,
      getExpanded,
      subscribe,
    }),
    [
      allSectionsClosed,
      bulkAction,
      bulkNonce,
      collapseAll,
      expandAll,
      getExpanded,
      registerSection,
      subscribe,
      updateSectionExpanded,
    ]
  );

  return (
    <FunnelSectionsVisibilityContext.Provider value={value}>
      {children}
    </FunnelSectionsVisibilityContext.Provider>
  );
};
